import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import { CARD_TABS, cardIdFromQuery, cardTabFromQuery, type CardTab } from "../lib/cardLinks";
import {
  BENEFIT_COLUMNS,
  CAP_COLUMNS,
  RATE_COLUMNS,
  breakEven,
  columnsFor,
  createCard,
  createCredit,
  deleteCard,
  endingText,
  fetchCards,
  renewsText,
  splitByFee,
  walletTotals,
  updateCard,
  updateCredit,
  type CadenceOption,
  type CardDto,
  type CardsDto,
  type Column,
  type CreditRow,
  type DetailRow,
} from "../lib/cardsPage";
import { Fold } from "../components/Fold";
import { Field, Select } from "../components/Form";
import { PageTitle, SectionTitle } from "../components/Heading";
import { Sheet } from "../components/Sheet";
import { CardOffers } from "../components/CardOffers";
import { WhichCard } from "../components/WhichCard";
import { netTone, standingText } from "../lib/dashboardPage";
import { formatWhole } from "../lib/money";

/**
 * Cards page.
 *
 * Rendering and form state only. Validation and the delete confirmation are
 * enforced on the server; this component's typed-name prompt is a courtesy,
 * not the guard.
 *
 * Which card lives here too (round 3 mockup, 2026-09-10): its fee table and
 * these tiles showed the same numbers twice. The category list leads and
 * folds away; each tile opens linkable details in a sheet.
 */
export default function Cards() {
  const [dto, setDto] = useState<CardsDto | null>(null);
  // Two errors, deliberately. A failed LOAD means there is no list to show, so
  // the page becomes the error plus a retry. A failed WRITE means the list is
  // still perfectly good -- and routing both through one state meant a rejected
  // "Add card" replaced the whole page, destroying the card list AND whatever
  // was typed into the form, for an error the form itself should have shown.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [writeError, setWriteError] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const open = cardIdFromQuery(params.get("card"));
  const detailTab = cardTabFromQuery(params.get("cardTab"));
  const openDetails = (id: number) => {
    const next = new URLSearchParams(params);
    next.set("card", String(id));
    next.delete("cardTab");
    navigate({ pathname: location.pathname, search: `?${next}`, hash: location.hash }, {
      state: { walletCardSheetOrigin: location.pathname + location.search + location.hash },
    });
  };
  const closeDetails = (deleted = false) => {
    // Only a sheet opened here adds a history entry. A bookmarked deep link
    // closes in place instead of navigating out of the app.
    if (!deleted && location.state?.walletCardSheetOrigin) {
      navigate(-1);
      return;
    }
    const next = new URLSearchParams(params);
    next.delete("card");
    next.delete("cardTab");
    setParams(next, { replace: true, state: null });
  };
  const selectDetailTab = (tab: CardTab) => {
    const next = new URLSearchParams(params);
    if (tab === "credits") next.delete("cardTab");
    else next.set("cardTab", tab);
    setParams(next, { replace: true, state: location.state });
  };
  // Add card opens in a sheet: lists first, forms on demand (2026-09-10).
  const [adding, setAdding] = useState(false);
  const showNoFee = params.get("noFee") === "1";
  const setShowNoFee = (show: boolean) => {
    const next = new URLSearchParams(params);
    if (show) next.set("noFee", "1");
    else next.delete("noFee");
    setParams(next, { replace: true, state: location.state });
  };

  const load = useCallback(() => {
    fetchCards()
      .then((d) => {
        setDto(d);
        setLoadError(null);
      })
      .catch((e: unknown) =>
        setLoadError(e instanceof Error ? e.message : String(e)),
      );
  }, []);

  useEffect(load, [load]);

  const reload = useCallback(() => {
    setWriteError(null);
    load();
  }, [load]);

  if (loadError) {
    return (
      <div className="space-y-3">
        <div className="rounded-md border border-red-900 bg-red-950/40 p-4 text-sm">
          {loadError}
        </div>
        <button
          onClick={() => {
            setLoadError(null);
            load();
          }}
          className="btn"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!dto) return <div className="text-sm text-neutral-400">Loading…</div>;

  const { withFee, noFee } = splitByFee(dto.cards);
  const openCard = dto.cards.find((c) => c.id === open) ?? null;
  const totals = walletTotals(dto.cards);
  const walletNet = totals.annual_fees - totals.credits_used;
  const tile = (card: CardDto) => (
    <CardTile key={card.id} card={card} onOpen={() => openDetails(card.id)} />
  );

  return (
    <section className="space-y-6">
      <PageTitle>Cards</PageTitle>

      {writeError && (
        <div className="rounded-md border border-red-900 bg-red-950/40 p-3 text-sm">
          {writeError}
        </div>
      )}

      {/* grid-cols-1 is minmax(0, 1fr): a bare `grid` sizes its one column to
          the longest card name, which pushed the rates off a 390px screen. */}
      <div className="grid grid-cols-1 gap-10 md:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)] md:items-start">
        <WhichCard knownCardIds={new Set(dto.cards.map((c) => c.id))} onOpenCard={openDetails} />

        <section className="space-y-3">
          <div className="flex items-center gap-3">
            <SectionTitle>Your cards</SectionTitle>
            <span className="grow" />
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="btn btn-primary btn-sm"
            >
              + Add card
            </button>
          </div>

          {/* Every fee against every credit used this fee year: the old fee
              table's headline, over the tiles that now carry its rows. */}
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span
              className={`hero-num text-[34px] ${
                netTone(walletNet) === "good" ? "text-green-400" : "text-red-400"
              }`}
            >
              {standingText(walletNet)}
            </span>
            <span className="text-sm text-neutral-400">
              {formatWhole(totals.credits_used)} in credits used against{" "}
              {formatWhole(totals.annual_fees)} in fees this fee year
            </span>
          </div>

          <div className="grid grid-cols-1 gap-3">{withFee.map(tile)}</div>

          {noFee.length > 0 && (
            <>
              <Fold
                open={showNoFee}
                onClick={() => setShowNoFee(!showNoFee)}
                label={`No annual fee · ${noFee.length} ${noFee.length === 1 ? "card" : "cards"}`}
              />
              {showNoFee && <div className="grid grid-cols-1 gap-3">{noFee.map(tile)}</div>}
            </>
          )}
        </section>
      </div>

      {open !== null && !openCard && (
        <div role="alert" className="rounded-xl border border-neutral-800 p-3 text-sm">
          <p>This card is no longer available.</p>
          <button type="button" className="btn btn-sm mt-2" onClick={() => closeDetails(true)}>
            Back to cards
          </button>
        </div>
      )}

      {openCard && (
        <Sheet title={openCard.header.display_name} onClose={() => closeDetails()} wide>
          <CardBody
            key={openCard.id}
            card={openCard}
            tab={detailTab}
            onTab={selectDetailTab}
            options={dto.options}
            onChanged={reload}
            onDeleted={() => {
              closeDetails(true);
              reload();
            }}
            onError={setWriteError}
          />
        </Sheet>
      )}

      {adding && (
        <Sheet title="Add card" onClose={() => setAdding(false)}>
          <AddCard
            onAdded={() => {
              setAdding(false);
              reload();
            }}
          />
        </Sheet>
      )}
    </section>
  );
}

/**
 * One card at rest: its fee, when the fee renews, how far this fee year's
 * redemptions are from covering it, and any benefit on it ending soon.
 * Tapping it opens the card's details in a sheet.
 */
function CardTile({ card, onOpen }: { card: CardDto; onOpen: () => void }) {
  const h = card.header;
  const fy = card.fee_year;
  const verdict = breakEven(fy.used, h.annual_fee);
  const renews = renewsText(fy.renews);
  const covered = h.annual_fee > 0 && fy.used >= h.annual_fee;
  const pct =
    h.annual_fee > 0 ? Math.min(100, Math.round((fy.used / h.annual_fee) * 100)) : 0;

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-haspopup="dialog"
      // grid-cols-1 is minmax(0, 1fr). A bare `grid` sizes its column to the
      // longest card name, pushing the fee off the tile on a phone.
      className="grid w-full grid-cols-1 gap-2 rounded-2xl border border-neutral-800/70 bg-neutral-900 p-4 text-left hover:border-neutral-700"
    >
      <span className="flex items-baseline gap-3">
        <span className="min-w-0 grow leading-snug font-bold">{h.display_name}</span>
        <span className="shrink-0 text-sm tabular-nums text-neutral-400">
          {h.annual_fee > 0 ? formatWhole(h.annual_fee) : "No annual fee"}
          {renews && ` · ${renews}`}
        </span>
      </span>
      {h.annual_fee > 0 && (
        <span className="block h-1 overflow-hidden rounded bg-neutral-800">
          <span
            className={`block h-1 ${covered ? "bg-green-400/80" : "bg-amber-400/80"}`}
            style={{ width: `${pct}%` }}
          />
        </span>
      )}
      <span className="flex items-baseline gap-3 text-xs text-neutral-400">
        {verdict && <span className={covered ? "text-green-400" : ""}>{verdict}</span>}
        <span className="ml-auto tabular-nums">
          {formatWhole(fy.used)} of {formatWhole(h.annual_credit_value)} used
        </span>
      </span>
      {(card.ending ?? []).map((e) => (
        <span key={`${e.credit}-${e.end_date}`} className="text-xs text-amber-400">
          {endingText(e)}
        </span>
      ))}
    </button>
  );
}

function CardBody({
  card,
  tab,
  onTab,
  options,
  onChanged,
  onDeleted,
  onError,
}: {
  card: CardDto;
  tab: CardTab;
  onTab: (tab: CardTab) => void;
  options: CardsDto["options"];
  onChanged: () => void;
  onDeleted: () => void;
  onError: (m: string) => void;
}) {
  return (
    <div className="text-sm">
      <div className="inline-flex flex-wrap gap-0.5 rounded-[10px] border border-neutral-700 p-0.5">
        {(Object.entries(CARD_TABS) as [CardTab, string][]).map(([t, label]) => (
          <button
            key={t}
            onClick={() => onTab(t)}
            aria-pressed={tab === t}
            className={
              "min-h-8 rounded-lg px-3 text-[13px] " +
              (tab === t
                ? "bg-neutral-800 text-neutral-100"
                : "text-neutral-400 hover:text-neutral-200")
            }
          >
            {label}
          </button>
        ))}
      </div>

      <div className="space-y-4 pt-3">
        {tab === "credits" && (
          <>
            <CreditList credits={card.credits} onSaved={onChanged} onError={onError} />
            <AddCredit
              cardId={card.id}
              options={options}
              onAdded={onChanged}
              onError={onError}
            />
          </>
        )}

        {tab === "offers" && (
          <CardOffers cardId={card.id} />
        )}

        {tab === "benefits" && (
          <Detail label="Benefits" rows={card.benefits} spec={BENEFIT_COLUMNS} />
        )}

        {tab === "rewards" && (
          <>
            <Detail label="Earning rates" rows={card.rates} spec={RATE_COLUMNS} />
            <Detail label="Spending caps" rows={card.caps} spec={CAP_COLUMNS} />
            <p className="text-xs text-neutral-500">Balances are tracked by loyalty program, separately from this card's earning rates.</p>
            <Link to="/points" className="btn btn-ghost btn-sm inline-flex">View loyalty balances</Link>
          </>
        )}

        {tab === "edit" && (
          <div className="space-y-4">
            <EditCard card={card} onSaved={onChanged} onError={onError} />
            <DeleteCard card={card} onDeleted={onDeleted} onError={onError} />
          </div>
        )}
      </div>
    </div>
  );
}

function CreditList({
  credits,
  onSaved,
  onError,
}: {
  credits: CreditRow[];
  onSaved: () => void;
  onError: (m: string) => void;
}) {
  if (credits.length === 0) {
    return <p className="text-neutral-500">No credits on this card.</p>;
  }
  return (
    <div className="divide-y divide-neutral-800">
      {credits.map((c) => (
        <div key={c.id} className="space-y-1 py-2 first:pt-0">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3">
            <span className="font-medium">{c.name}</span>
            <span className="tabular-nums text-neutral-400">
              ${c.amount.toFixed(2)}/{c.cadence_label} ·{" "}
              {formatWhole(c.annual_value)}/yr · {c.reset_label}
            </span>
          </div>
          {c.category && <p className="text-xs text-neutral-500">{c.category}</p>}
          {c.notes_line && <p className="text-xs text-neutral-500">{c.notes_line}</p>}
          <RecurringDay credit={c} onSaved={onSaved} onError={onError} />
        </div>
      ))}
    </div>
  );
}

/**
 * The per-credit "recurring post day", which prefills the Mark Used date on
 * the Credits page, and the optional month that makes it a once-a-year date
 * (an annual membership renewal).
 *
 * Save is disabled until the value actually differs from what was loaded. The
 * server refuses an empty PATCH with a 422, so a Save that sent the unchanged
 * value would still write a row for no reason, and this is the one control on
 * the page a stray click lands on.
 */
function RecurringDay({
  credit,
  onSaved,
  onError,
}: {
  credit: CreditRow;
  onSaved: () => void;
  onError: (m: string) => void;
}) {
  const [day, setDay] = useState(String(credit.recurring_post_day));
  const [month, setMonth] = useState(String(credit.recurring_post_month));
  const [busy, setBusy] = useState(false);
  const dayChanged = Number(day) !== credit.recurring_post_day;
  const monthChanged = Number(month) !== credit.recurring_post_month;
  const changed = dayChanged || monthChanged;

  return (
    <div className="flex flex-wrap items-end gap-2">
      <label className="flex flex-col gap-1 text-xs text-neutral-400">
        Recurring post day
        <input
          type="number"
          min={0}
          max={31}
          value={day}
          onChange={(e) => setDay(e.target.value)}
          className="min-h-10 w-20 rounded-lg border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm text-neutral-100"
        />
      </label>
      <label className="flex flex-col gap-1 text-xs text-neutral-400">
        Post month
        <input
          type="number"
          min={0}
          max={12}
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="min-h-10 w-20 rounded-lg border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm text-neutral-100"
        />
      </label>
      <button
        type="button"
        disabled={!changed || busy}
        onClick={() => {
          setBusy(true);
          // 0 means "no predictable day" (or month), and the column holds
          // NULL for that. Sending 0 would make this app the only writer of a
          // day-zero row. Only what changed is sent.
          const fields: Record<string, number | null> = {};
          if (dayChanged) fields.recurring_post_day = Number(day) || null;
          if (monthChanged) fields.recurring_post_month = Number(month) || null;
          updateCredit(credit.id, fields)
            .then(onSaved)
            .catch((e: unknown) =>
              onError(e instanceof Error ? e.message : String(e)),
            )
            .finally(() => setBusy(false));
        }}
        className="btn btn-ghost btn-sm"
      >
        {busy ? "Saving…" : "Save"}
      </button>
      <span className="text-xs text-neutral-600">
        Day of month this credit posts. 0 = varies. A month makes it once a
        year (e.g. a 12/14 renewal); 0 = every period.
      </span>
    </div>
  );
}

function AddCredit({
  cardId,
  options,
  onAdded,
  onError,
}: {
  cardId: number;
  options: CardsDto["options"];
  onAdded: () => void;
  onError: (m: string) => void;
}) {
  const first = (o: CadenceOption[]) => (o.length > 0 ? o[0].value : "");
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("0");
  const [cadence, setCadence] = useState(first(options.cadences));
  const [reset, setReset] = useState(first(options.reset_schedules));
  const [category, setCategory] = useState("");
  const [notes, setNotes] = useState("");
  const [day, setDay] = useState("0");
  const [busy, setBusy] = useState(false);

  return (
    <form
      // Named, because this page renders three forms whose fields overlap by
      // label: "Issuer" and "Annual fee" appear on both card forms, "Name" on
      // all three. Without a name on each form those labels are ambiguous to
      // anything reading the page by structure, a screen reader included.
      aria-label="Add credit"
      className="space-y-3 rounded-xl border border-neutral-800 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        createCredit(cardId, {
          name,
          amount: Number(amount) || 0,
          cadence,
          reset_schedule: reset,
          category,
          special_notes: notes,
          recurring_post_day: Number(day) || 0,
        })
          .then(() => {
            setName("");
            setAmount("0");
            setCategory("");
            setNotes("");
            setDay("0");
            onAdded();
          })
          .catch((err: unknown) =>
            onError(err instanceof Error ? err.message : String(err)),
          )
          .finally(() => setBusy(false));
      }}
    >
      <SectionTitle>Add credit</SectionTitle>
      <div className="grid gap-2 sm:grid-cols-2">
        <Field label="Name" value={name} onChange={setName} />
        <Field label="Amount" value={amount} onChange={setAmount} type="number" />
        <Select
          label="Cadence"
          value={cadence}
          options={options.cadences}
          onChange={setCadence}
        />
        <Select
          label="Reset schedule"
          value={reset}
          options={options.reset_schedules}
          onChange={setReset}
        />
        <Field label="Category" value={category} onChange={setCategory} />
        <Field label="Notes" value={notes} onChange={setNotes} />
        <Field
          label="Recurring post day (0 = varies)"
          value={day}
          onChange={setDay}
          type="number"
        />
      </div>
      <button
        type="submit"
        disabled={busy || !name}
        className="btn btn-primary"
      >
        {busy ? "Adding…" : "Add credit"}
      </button>
    </form>
  );
}

/**
 * The Edit Card form.
 *
 * Seeded from `edit_defaults`, which is what the live page's form is seeded
 * from. There is no URL field here and there must not be: the server's edit
 * model does not accept one, precisely so an edit form that never showed a URL
 * cannot blank one that was set elsewhere.
 */
function EditCard({
  card,
  onSaved,
  onError,
}: {
  card: CardDto;
  onSaved: () => void;
  onError: (m: string) => void;
}) {
  const d = card.edit_defaults;
  const [name, setName] = useState(d.name);
  const [issuer, setIssuer] = useState(d.issuer);
  const [fee, setFee] = useState(String(d.annual_fee));
  const [anniversary, setAnniversary] = useState(d.anniversary_date);
  const [notes, setNotes] = useState(d.notes);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  return (
    <form
      aria-label="Edit card"
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        setSaved(false);
        updateCard(card.id, {
          name,
          issuer,
          annual_fee: Number(fee) || 0,
          anniversary_date: anniversary,
          notes,
        })
          .then(() => {
            setSaved(true);
            onSaved();
          })
          .catch((err: unknown) =>
            onError(err instanceof Error ? err.message : String(err)),
          )
          .finally(() => setBusy(false));
      }}
    >
      <SectionTitle>Edit card</SectionTitle>
      <div className="grid gap-2 sm:grid-cols-2">
        <Field label="Name" value={name} onChange={setName} />
        <Field label="Issuer" value={issuer} onChange={setIssuer} />
        <Field label="Annual fee" value={fee} onChange={setFee} type="number" />
        <Field
          label="Anniversary date (MM-DD)"
          value={anniversary}
          onChange={setAnniversary}
        />
        <Field label="Notes" value={notes} onChange={setNotes} />
      </div>
      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={busy}
          className="btn btn-primary"
        >
          {busy ? "Saving…" : "Save changes"}
        </button>
        {saved && <span className="text-xs text-green-400">Saved.</span>}
      </div>
    </form>
  );
}

function Detail({
  label,
  rows,
  spec,
}: {
  label: string;
  rows: DetailRow[];
  spec: Column[];
}) {
  if (rows.length === 0) {
    return (
      <div>
        <SectionTitle>{label}</SectionTitle>
        <p className="text-neutral-500">None recorded.</p>
      </div>
    );
  }
  const columns = columnsFor(rows, spec);
  return (
    <div>
      <SectionTitle>{label}</SectionTitle>
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead className="text-neutral-400">
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={
                    "px-2 py-1 font-normal " +
                    (c.align === "right" ? "text-right" : "")
                  }
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="border-t border-neutral-800">
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={
                      "px-2 py-1 " +
                      (c.align === "right" ? "text-right tabular-nums" : "")
                    }
                  >
                    {row[c.key] === null || row[c.key] === undefined
                      ? "—"
                      : (c.format ?? String)(row[c.key])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AddCard({ onAdded }: { onAdded: () => void }) {
  const [name, setName] = useState("");
  const [issuer, setIssuer] = useState("");
  const [fee, setFee] = useState("0");
  const [anniversary, setAnniversary] = useState("");
  const [urlIssuer, setUrlIssuer] = useState("");
  const [urlReview, setUrlReview] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  // Shown inside the sheet. A rejected add keeps the sheet and everything
  // typed into it, so the retry is not from memory.
  const [formError, setFormError] = useState<string | null>(null);

  return (
    <form
      aria-label="Add card"
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        setFormError(null);
        // The four fields below the fold are not decoration. The live form has
        // seven inputs and this one had three, so a card added from here
        // arrived with no anniversary date -- which is what `cardmember_year`
        // credits reset on -- and no issuer URL.
        createCard({
          name,
          issuer,
          annual_fee: Number(fee) || 0,
          anniversary_date: anniversary,
          url_issuer: urlIssuer,
          url_review: urlReview,
          notes,
        })
          .then(() => {
            setName("");
            setIssuer("");
            setFee("0");
            setAnniversary("");
            setUrlIssuer("");
            setUrlReview("");
            setNotes("");
            onAdded();
          })
          .catch((err: unknown) =>
            setFormError(err instanceof Error ? err.message : String(err)),
          )
          .finally(() => setBusy(false));
      }}
    >
      <div className="grid gap-2 sm:grid-cols-2">
        <Field label="Card name" value={name} onChange={setName} />
        <Field label="Issuer" value={issuer} onChange={setIssuer} />
        <Field label="Annual fee" value={fee} onChange={setFee} type="number" />
        <Field
          label="Anniversary date (MM-DD)"
          value={anniversary}
          onChange={setAnniversary}
        />
        <Field label="Issuer benefit page URL" value={urlIssuer} onChange={setUrlIssuer} />
        <Field label="Review page URL" value={urlReview} onChange={setUrlReview} />
        <Field label="Notes" value={notes} onChange={setNotes} />
      </div>
      {formError && <p className="text-sm text-red-400">{formError}</p>}
      <button
        type="submit"
        disabled={busy}
        className="btn btn-primary"
      >
        {busy ? "Adding…" : "Add card"}
      </button>
    </form>
  );
}

function DeleteCard({
  card,
  onDeleted,
  onError,
}: {
  card: CardDto;
  onDeleted: () => void;
  onError: (m: string) => void;
}) {
  const [typed, setTyped] = useState("");
  const [arming, setArming] = useState(false);
  const expected = card.edit_defaults.name;

  if (!arming) {
    return (
      <button
        onClick={() => setArming(true)}
        className="btn btn-danger btn-sm"
      >
        Delete this card
      </button>
    );
  }

  return (
    <div className="space-y-2 rounded-md border border-red-900 bg-red-950/30 p-3">
      <p>
        This deletes <strong>{expected}</strong>, every credit on it, and their
        recorded usage history ({formatWhole(card.header.annual_credit_value)} of
        annual credits). There is no undo.
      </p>
      <p className="text-neutral-400">
        Type the card&apos;s name to confirm:
      </p>
      <div className="flex flex-wrap gap-2">
        <input
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          className="min-h-10 min-w-0 rounded-lg border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm"
          placeholder={expected}
        />
        <button
          // Disabled until the name matches, and the SERVER checks it again --
          // this is a courtesy, not the guard.
          disabled={typed !== expected}
          onClick={() =>
            deleteCard(card.id, typed)
              .then(onDeleted)
              .catch((e: unknown) =>
                onError(e instanceof Error ? e.message : String(e)),
              )
          }
          className="btn btn-destroy"
        >
          Delete permanently
        </button>
        <button
          onClick={() => {
            setArming(false);
            setTyped("");
          }}
          className="btn btn-ghost"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
